// ─────────────────────────────────────────────────────────────────────────────
// PRN → drawable geometry.
//
// The backend (utils/prnTemplate.js) owns *values*: which loan field a token
// resolves to, and what the transforms make of it. This module owns *layout*:
// where on the tag each field lands, how tall its font is, which field is a
// barcode. Together they let the mapper screen show the installer the actual
// sticker instead of a wall of printer commands.
//
// Deliberately no value logic here — element text keeps its <TOKEN> placeholders
// and the previewer substitutes them from the backend's `resolved` map, so the
// preview can never disagree with what gets printed.
//
// Coordinates are printer dots throughout (203 dpi = 8 dots/mm), which is the
// unit every command in a PRN file already speaks.
// ─────────────────────────────────────────────────────────────────────────────

export const DPMM = 8; // dots per mm at 203 dpi

// Seagull/BarTender wrap the printer language in <xpml> driver blocks; a single
// file can hold several complete designs separated by an <end/> marker. Mirrors
// splitVariants/stripXpml on the backend so the previewer and the print job look
// at the same bytes.
const XPML_BLOCK = /<xpml>[\s\S]*?<\/xpml>/g;
const XPML_END = /<xpml><end\s*\/><\/xpml>/g;

// Built rather than written as a literal: an ESC byte inside a regex literal
// trips eslint's no-control-regex.
const SBPL_RE = new RegExp("\\x1bA[\\s\\S]*\\x1bZ");

export function splitVariants(raw) {
  const parts = String(raw || "")
    .split(XPML_END)
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length ? parts : [String(raw || "").trim()];
}

export function stripXpml(source) {
  return String(source || "")
    .replace(XPML_BLOCK, "\n")
    .replace(/\n{2,}/g, "\n")
    .trim();
}

export function detectLanguage(body) {
  if (/\^XA[\s\S]*\^XZ/.test(body)) return "ZPL";
  if (/^\s*SIZE\s|\bCLS\b|^\s*PRINT\s+\d/m.test(body)) return "TSPL";
  if (SBPL_RE.test(body)) return "SBPL";
  return "UNKNOWN";
}

// ZPL rotation letters → degrees clockwise.
const rotDeg = (r) => {
  switch (String(r || "N").toUpperCase()) {
    case "R":
      return 90;
    case "I":
      return 180;
    case "B":
      return 270;
    default:
      return 0;
  }
};

// ─── ZPL ────────────────────────────────────────────────────────────────────

// Every command is ^ or ~ followed by two characters; its parameters run to the
// next command. Safe against field data because ZPL forbids a bare ^ inside it.
const ZPL_CMD = /[\^~]([A-Za-z0-9@]{2})([^\^~]*)/g;

// ^BQ carries its error-correction level and mask ahead of the payload, e.g.
// ^FDQA,1847^FS. Strip that so the preview encodes the data alone.
const stripQrPrefix = (s) => String(s || "").replace(/^[HQML][AM]?,/i, "");

function parseZpl(body) {
  const elements = [];
  const notes = new Set();

  let widthDots = null;
  let lengthDots = null;
  const home = { x: 0, y: 0 };

  // ^CF sets the font every later ^FD inherits; ^FW the default rotation;
  // ^BY the module width / wide-narrow ratio / height every barcode inherits.
  let cf = { face: "0", h: 20, w: 0 };
  let fw = "N";
  let by = { module: 2, ratio: 3, height: 80 };

  // Per-field state, all cleared by ^FS.
  let origin = null;
  let font = null;
  let bc = null;
  let invert = false;
  let id = 0;

  for (const m of body.matchAll(ZPL_CMD)) {
    const cmd = m[1].toUpperCase();
    const raw = m[2] || "";
    const p = raw.split(",");
    const n = (i, d) => {
      const v = parseFloat(p[i]);
      return Number.isFinite(v) ? v : d;
    };
    const flag = (i, d = false) => {
      const v = (p[i] || "").trim().toUpperCase();
      return v ? v === "Y" : d;
    };

    // ^A<face><rotation>,height,width — the only command starting with A.
    if (cmd[0] === "A") {
      font = {
        face: cmd[1],
        rot: (p[0] || fw).toUpperCase(),
        h: n(1, cf.h),
        w: n(2, 0),
      };
      continue;
    }

    switch (cmd) {
      case "PW":
        widthDots = n(0, null);
        break;
      case "LL":
        lengthDots = n(0, null);
        break;
      case "LH":
        home.x = n(0, 0);
        home.y = n(1, 0);
        break;
      case "CF":
        cf = { face: p[0] || cf.face, h: n(1, cf.h), w: n(2, 0) };
        break;
      case "FW":
        fw = (p[0] || "N").toUpperCase();
        break;
      case "BY":
        by = { module: n(0, 2), ratio: n(1, 3), height: n(2, by.height) };
        break;
      case "FO":
      case "FT":
        origin = { x: n(0, 0), y: n(1, 0), baseline: cmd === "FT" };
        break;
      case "FR":
        invert = true;
        break;

      case "BC":
        bc = {
          symbology: "code128",
          rot: p[0] || fw,
          height: n(1, by.height),
          human: flag(2, true),
          above: flag(3),
        };
        break;
      case "B3":
        bc = {
          symbology: "code39",
          rot: p[0] || fw,
          height: n(2, by.height),
          human: flag(3, true),
          above: flag(4),
        };
        break;
      case "BQ":
        bc = { symbology: "qr", rot: p[0] || fw, mag: n(2, 3) };
        break;

      case "GB": {
        const w = n(0, 0);
        const h = n(1, 0);
        const t = n(2, 1) || 1;
        elements.push({
          id: ++id,
          kind: "box",
          x: (origin?.x ?? 0) + home.x,
          y: (origin?.y ?? 0) + home.y,
          w: Math.max(w, t),
          h: Math.max(h, t),
          thickness: t,
          // ZPL draws a solid bar when the box is no thicker than its border —
          // that is how designs get their separator lines.
          filled: w <= t || h <= t,
        });
        break;
      }

      case "GF":
      case "IM":
      case "XG":
        notes.add(
          "The design contains an embedded image or logo, which the preview cannot draw.",
        );
        break;

      case "FD":
      case "FV": {
        const x = (origin?.x ?? 0) + home.x;
        const y = (origin?.y ?? 0) + home.y;
        if (bc && bc.symbology === "qr") {
          elements.push({
            id: ++id,
            kind: "qr",
            x,
            y,
            // ^FT anchors a barcode by its *bottom* left corner. The size of a
            // QR is not known until its payload is, so the previewer applies the
            // offset once it has encoded it.
            baseline: !!origin?.baseline,
            mag: Math.max(1, bc.mag || 3),
            rot: rotDeg(bc.rot),
            raw: stripQrPrefix(raw),
          });
        } else if (bc) {
          const height = bc.height || by.height;
          elements.push({
            id: ++id,
            kind: "barcode",
            symbology: bc.symbology,
            x,
            // Bar height is known here, so the bottom-left anchor is normalised
            // away and every element in the list carries a top-left origin.
            y: origin?.baseline ? y - height : y,
            height,
            module: Math.max(1, by.module),
            ratio: by.ratio,
            human: bc.human,
            above: bc.above,
            rot: rotDeg(bc.rot),
            raw,
          });
        } else {
          const f = font || { face: cf.face, rot: fw, h: cf.h, w: cf.w };
          elements.push({
            id: ++id,
            kind: "text",
            x,
            y,
            h: f.h || 20,
            w: f.w || 0,
            mono: f.face !== "0",
            rot: rotDeg(f.rot),
            baseline: !!origin?.baseline,
            invert,
            raw,
          });
        }
        break;
      }

      case "FS":
        origin = null;
        font = null;
        bc = null;
        invert = false;
        break;

      default:
        // Any other ^B* is a barcode symbology this previewer does not encode.
        // Drawn as Code 128 bars of the right size so the layout still reads
        // correctly, with a note that the pattern itself is indicative only.
        if (cmd[0] === "B" && cmd !== "BY") {
          bc = {
            symbology: "code128",
            approx: cmd,
            rot: p[0] || fw,
            height: by.height,
            human: false,
          };
          notes.add(
            `^${cmd} is drawn as generic bars — the symbology is not encoded in the preview.`,
          );
        }
        break;
    }
  }

  return { elements, widthDots, lengthDots, notes: [...notes] };
}

// ─── TSPL ───────────────────────────────────────────────────────────────────

// Split on commas that sit outside quotes: TSPL text and barcode payloads are
// quoted and routinely contain commas.
function splitArgs(s) {
  const out = [];
  let cur = "";
  let q = false;
  for (const ch of String(s || "")) {
    if (ch === '"') {
      q = !q;
      cur += ch;
      continue;
    }
    if (ch === "," && !q) {
      out.push(cur.trim());
      cur = "";
      continue;
    }
    cur += ch;
  }
  out.push(cur.trim());
  return out;
}

const unq = (s) => String(s || "").replace(/^"|"$/g, "");

// TSPL's built-in bitmap fonts, in dots. Multiplied by the command's x/y
// multipliers to get the printed cell size.
const TSPL_FONTS = {
  1: { w: 8, h: 12 },
  2: { w: 12, h: 20 },
  3: { w: 16, h: 24 },
  4: { w: 24, h: 32 },
  5: { w: 32, h: 48 },
  6: { w: 14, h: 19 },
  7: { w: 21, h: 27 },
  8: { w: 14, h: 25 },
};

// SIZE takes inches by default, "mm" or "dot" when stated.
const sizeToDots = (s) => {
  const txt = String(s || "").trim();
  const mm = /([\d.]+)\s*mm/i.exec(txt);
  if (mm) return Number(mm[1]) * DPMM;
  const dot = /([\d.]+)\s*dot/i.exec(txt);
  if (dot) return Number(dot[1]);
  const v = parseFloat(txt);
  return Number.isFinite(v) ? v * 25.4 * DPMM : null;
};

function parseTspl(body) {
  const elements = [];
  const notes = new Set();
  let widthDots = null;
  let lengthDots = null;
  let id = 0;

  for (const line of String(body).split(/\r?\n/)) {
    const t = line.trim();
    if (!t) continue;

    const sp = t.search(/\s/);
    const kw = (sp === -1 ? t : t.slice(0, sp)).toUpperCase();
    const a = splitArgs(sp === -1 ? "" : t.slice(sp + 1));
    const n = (i, d) => {
      const v = parseFloat(a[i]);
      return Number.isFinite(v) ? v : d;
    };
    // Every drawing command puts its payload last, whatever optional alignment
    // or justification arguments the vendor's exporter chose to include.
    const payload = () => unq(a[a.length - 1]);

    switch (kw) {
      case "SIZE":
        widthDots = sizeToDots(a[0]);
        lengthDots = sizeToDots(a[1]);
        break;

      case "TEXT":
      case "BLOCK": {
        const isBlock = kw === "BLOCK";
        // BLOCK inserts width/height before the font argument.
        const fi = isBlock ? 4 : 2;
        const face = unq(a[fi]);
        const xmul = n(fi + 2, 1);
        const ymul = n(fi + 3, 1);
        const base = TSPL_FONTS[face];
        elements.push({
          id: ++id,
          kind: "text",
          x: n(0, 0),
          y: n(1, 0),
          // Font "0" is the scalable one: its multipliers are point sizes.
          h: base ? base.h * ymul : ymul * (25.4 / 72) * DPMM,
          w: base ? base.w * xmul : 0,
          mono: !!base,
          rot: n(fi + 1, 0),
          baseline: false,
          raw: payload(),
        });
        break;
      }

      case "BARCODE": {
        const type = unq(a[2]).toUpperCase();
        elements.push({
          id: ++id,
          kind: "barcode",
          symbology: type.includes("39") ? "code39" : "code128",
          approx: /12[08]|39/.test(type) ? null : type,
          x: n(0, 0),
          y: n(1, 0),
          height: n(3, 60),
          human: n(4, 1) !== 0,
          rot: n(5, 0),
          module: Math.max(1, n(6, 2)),
          ratio: Math.max(2, Math.round(n(7, 6) / Math.max(1, n(6, 2)))),
          above: false,
          raw: payload(),
        });
        if (!/12[08]|39/.test(type))
          notes.add(
            `Barcode type "${type}" is drawn as generic bars — the symbology is not encoded in the preview.`,
          );
        break;
      }

      case "QRCODE":
        elements.push({
          id: ++id,
          kind: "qr",
          x: n(0, 0),
          y: n(1, 0),
          mag: Math.max(1, n(3, 4)),
          rot: n(5, 0),
          raw: payload(),
        });
        break;

      case "BAR":
        elements.push({
          id: ++id,
          kind: "box",
          x: n(0, 0),
          y: n(1, 0),
          w: n(2, 0),
          h: n(3, 0),
          thickness: 0,
          filled: true,
        });
        break;

      case "BOX":
        elements.push({
          id: ++id,
          kind: "box",
          x: n(0, 0),
          y: n(1, 0),
          w: n(2, 0) - n(0, 0),
          h: n(3, 0) - n(1, 0),
          thickness: n(4, 1) || 1,
          filled: false,
        });
        break;

      case "PUTBMP":
      case "PUTPCX":
      case "DOWNLOAD":
        notes.add(
          "The design contains an embedded image or logo, which the preview cannot draw.",
        );
        break;

      case "DIRECTION":
        if (n(0, 0) === 1)
          notes.add(
            "DIRECTION 1 prints the label rotated 180° — the preview shows the unrotated design.",
          );
        break;

      default:
        break;
    }
  }

  return { elements, widthDots, lengthDots, notes: [...notes] };
}

// ─── Entry point ────────────────────────────────────────────────────────────

/**
 * Parse one variant of a PRN source into drawable elements.
 *
 * @param {string} source     the stored template, driver wrapper and all
 * @param {number} [variant]  1-based variant number (a file can hold several)
 * @returns {{
 *   language: string,
 *   elements: Array<object>,
 *   declared: {wMm: number|null, hMm: number|null},
 *   notes: string[],
 * }}  element coordinates and sizes are printer dots
 */
export function parseLabel(source, variant = 1) {
  const empty = {
    language: "UNKNOWN",
    elements: [],
    declared: { wMm: null, hMm: null },
    notes: [],
  };
  if (!source || !String(source).trim()) return empty;

  const variants = splitVariants(source);
  const chosen =
    variants[Math.min(Math.max(1, variant || 1), variants.length) - 1];
  const body = stripXpml(chosen);
  const language = detectLanguage(body);

  let parsed;
  if (language === "ZPL") parsed = parseZpl(body);
  else if (language === "TSPL") parsed = parseTspl(body);
  else
    return {
      ...empty,
      language,
      notes: [
        language === "SBPL"
          ? "This is a Sato-native (SBPL) template. Its keywords still map and print correctly, but the preview cannot draw SBPL layouts."
          : "The printer language of this template was not recognised, so its layout cannot be drawn.",
      ],
    };

  const mm = (dots) => (dots ? +(dots / DPMM).toFixed(1) : null);
  return {
    language,
    elements: parsed.elements,
    declared: { wMm: mm(parsed.widthDots), hMm: mm(parsed.lengthDots) },
    notes: parsed.notes,
  };
}
